import React, { useState, useEffect } from 'react';
import { 
  Search, 
  X, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  AlertCircle,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { 
  format, 
  addDays, 
  startOfDay, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  getDay 
} from 'date-fns';
import { es } from 'date-fns/locale';
import { isSlotValidForProf } from '../hooks/useCalendar';

const getLocalISODate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FreeSlotFinder = ({ isOpen, onClose, onSelectSlot, currentProfessional, professionalsList = [], onGoToDate }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(currentProfessional || 'VER TODOS');
  const [profs, setProfs] = useState([]);
  
  // Monthly Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthSlotsMap, setMonthSlotsMap] = useState({}); // { 'YYYY-MM-DD': [ { time, professional } ] }

  useEffect(() => {
    if (!isOpen) return;
    if (professionalsList && professionalsList.length > 0) {
      setProfs(professionalsList.filter(p => !p.is_hidden));
    } else {
      supabase.from('professionals').select('*').then(({ data }) => {
        if (data) setProfs(data.filter(p => !p.is_hidden));
      });
    }
  }, [isOpen, professionalsList]);

  useEffect(() => {
    if (currentProfessional) {
      setSelectedProfessional(currentProfessional);
    }
  }, [currentProfessional]);

  // Scan full month for free slots
  const scanMonthSlots = async () => {
    setLoading(true);
    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

      const { data: appointments, error } = await supabase
        .from('appointments')
        .select('appointment_date, appointment_time, professional_name, status')
        .gte('appointment_date', getLocalISODate(monthStart))
        .lte('appointment_date', getLocalISODate(monthEnd))
        .neq('status', 'cancelado');

      if (error) throw error;

      const { data: holidaysData } = await supabase.from('holidays').select('*');
      const holidaysList = holidaysData || [];

      let profsToScan = profs;
      if (selectedProfessional && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedProfessional)) {
        profsToScan = profs.filter(p => p.full_name === selectedProfessional || p.name === selectedProfessional);
      }
      if (profsToScan.length === 0) profsToScan = profs;

      const map = {};

      for (const scanDate of daysInMonth) {
        const dateStr = getLocalISODate(scanDate);
        map[dateStr] = [];

        for (const profObj of profsToScan) {
          const profName = profObj.full_name || profObj.name;
          const step = parseInt(profObj.working_hours?.slot_duration || profObj.slot_duration || 15, 10);
          const validStep = (!isNaN(step) && step > 0) ? step : 15;
          const maxOverbooking = profObj.working_hours?.max_overbooking || 0;

          for (let m = 8 * 60; m < 20 * 60; m += validStep) {
            const timeStr = `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}:00`;
            const timeDisplay = timeStr.substring(0, 5);

            const isWorking = isSlotValidForProf(profObj, scanDate, timeDisplay, holidaysList);
            if (!isWorking) continue;

            const occupiedCount = (appointments || []).filter(a => 
              a.appointment_date === dateStr && 
              a.appointment_time?.substring(0, 5) === timeDisplay &&
              a.professional_name === profName
            ).length;

            if (occupiedCount < (1 + maxOverbooking)) {
              map[dateStr].push({
                time: timeDisplay,
                professional: profName,
                date: dateStr
              });
            }
          }
        }
      }

      setMonthSlotsMap(map);
    } catch (err) {
      console.error('Error scanning month slots:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && profs.length > 0) {
      scanMonthSlots();
    }
  }, [isOpen, currentMonth, selectedProfessional, profs]);

  if (!isOpen) return null;

  // Calendar Grid Days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Padding days for Monday start (1: Mon, ... 0: Sun)
  const startDayOfWeek = (getDay(monthStart) + 6) % 7; 
  const paddingDays = Array.from({ length: startDayOfWeek });

  const selectedDateStr = getLocalISODate(selectedDate);
  const slotsForSelectedDate = monthSlotsMap[selectedDateStr] || [];

  const handleGoToSelectedDate = () => {
    if (onGoToDate) {
      onGoToDate(selectedDate);
    } else if (onSelectSlot && slotsForSelectedDate.length > 0) {
      onSelectSlot(slotsForSelectedDate[0]);
    } else {
      // Fallback click
      onClose();
    }
  };

  return (
    <div className="appointment-modal-overlay">
      <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '820px', width: '95vw', overflowX: 'hidden' }}>
        <div className="appointment-modal-header premium-header">
          <div className="header-title">
            <div className="title-icon-wrapper green"><Search size={20} /></div>
            <span>BUSCADOR INTELIGENTE DE TURNOS LIBRES</span>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="appointment-modal-body" style={{ overflowX: 'hidden', padding: '1.5rem' }}>
          {/* Top Professional Filter */}
          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Profesional / Especialidad</label>
            <select value={selectedProfessional} onChange={(e) => setSelectedProfessional(e.target.value)} style={{ width: '100%', padding: '0.6rem 0.8rem' }}>
              <option value="VER TODOS">-- TODOS LOS PROFESIONALES --</option>
              {profs.map(p => (
                <option key={p.id} value={p.full_name}>{p.full_name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
            {/* Left: Monthly Calendar */}
            <div className="mini-calendar-box card glass-panel" style={{ padding: '1rem', borderRadius: '16px' }}>
              {/* Calendar Header Navigation */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <button 
                  onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.4rem', color: 'var(--text-primary)' }}
                >
                  <ChevronLeft size={18} />
                </button>
                <span style={{ fontWeight: '800', fontSize: '1rem', textTransform: 'capitalize', color: 'var(--text-bright)' }}>
                  {format(currentMonth, 'MMMM yyyy', { locale: es })}
                </span>
                <button 
                  onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.4rem', color: 'var(--text-primary)' }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Day Headers (L M X J V S D) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', fontWeight: '700', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                <div>L</div><div>M</div><div>X</div><div>J</div><div>V</div><div>S</div><div>D</div>
              </div>

              {/* Days Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                {paddingDays.map((_, i) => (
                  <div key={`pad-${i}`} style={{ height: '38px' }} />
                ))}
                {daysInMonth.map((dayDate, idx) => {
                  const dateStr = getLocalISODate(dayDate);
                  const isSelected = isSameDay(dayDate, selectedDate);
                  const hasFreeSlots = (monthSlotsMap[dateStr] || []).length > 0;
                  const dayNum = format(dayDate, 'd');

                  return (
                    <div 
                      key={idx}
                      onClick={() => setSelectedDate(dayDate)}
                      style={{ 
                        height: '38px',
                        display: 'flex',
                        flexDirection: 'column',
                        justify: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: isSelected ? '800' : '600',
                        position: 'relative',
                        background: isSelected ? '#3b82f6' : (hasFreeSlots ? 'rgba(0, 229, 255, 0.05)' : 'transparent'),
                        color: isSelected ? '#ffffff' : 'var(--text-primary)',
                        border: isSelected ? '2px solid #60a5fa' : '1px solid transparent',
                        transition: 'all 0.15s'
                      }}
                    >
                      <span>{dayNum}</span>
                      {hasFreeSlots && !isSelected && (
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', marginTop: '2px' }} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend Footer */}
              <div style={{ marginTop: '1.2rem', paddingTop: '0.8rem', borderTop: '1px solid var(--border-glass)', fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', color: 'var(--text-muted)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
                  <span>Día de Atención con Espacio Libre</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }} />
                  <span>Día Seleccionado</span>
                </div>
              </div>
            </div>

            {/* Right: Slots List for Selected Date */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <div>
                <div style={{ marginBottom: '0.8rem', borderBottom: '1px solid var(--border-glass)', paddingBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '700' }}>Fecha Seleccionada:</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: 'var(--primary-cyan)' }}>
                    {format(selectedDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                  </div>
                </div>

                <div style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: '0.75rem', color: 'var(--text-bright)' }}>
                  Día con Horarios Disponibles
                </div>

                <div style={{ maxHeight: '230px', overflowY: 'auto', paddingRight: '0.3rem' }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                      <RefreshCw size={20} className="animate-spin" style={{ marginBottom: '0.5rem' }} />
                      <p style={{ fontSize: '0.8rem' }}>Cargando disponibilidad...</p>
                    </div>
                  ) : slotsForSelectedDate.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                      <AlertCircle size={24} style={{ color: '#ef4444', marginBottom: '0.5rem' }} />
                      <p>No hay turnos libres registrados para este día.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '0.4rem' }}>
                      {slotsForSelectedDate.map((slot, idx) => (
                        <div 
                          key={idx}
                          onClick={() => onSelectSlot(slot)}
                          style={{ 
                            background: 'rgba(0, 229, 255, 0.08)',
                            border: '1px solid rgba(0, 229, 255, 0.2)',
                            borderRadius: '6px',
                            padding: '0.4rem 0.2rem',
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            color: 'var(--text-bright)',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                          className="mini-time-chip"
                          title={`Reservar a las ${slot.time} hs con ${slot.professional}`}
                        >
                          {slot.time}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer with IR A FECHA SELECCIONADA */}
        <div className="appointment-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn-cancel-modal" onClick={onClose} style={{ color: '#ff5252', fontWeight: '700' }}>CERRAR</button>
          
          <button 
            className="btn-primary-cyan-action" 
            onClick={handleGoToSelectedDate}
            style={{ 
              background: 'linear-gradient(135deg, #00e5ff 0%, #00b8cc 100%)',
              color: '#0b0b14',
              border: 'none',
              borderRadius: '10px',
              padding: '0.6rem 1.4rem',
              fontWeight: '800',
              fontSize: '0.85rem',
              letterSpacing: '0.04em',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 0 15px rgba(0, 229, 255, 0.4)',
              cursor: 'pointer'
            }}
          >
            IR A FECHA SELECCIONADA <ExternalLink size={16} />
          </button>
        </div>
      </div>

      <style>{`
        .mini-time-chip:hover {
          background: #00e5ff !important;
          color: #000000 !important;
          transform: translateY(-2px);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FreeSlotFinder;
